import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Prompt, PromptVariable } from './promptsData';

interface CreatePromptModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (prompt: Prompt) => void;
  editingPrompt?: Prompt | null;
}

const CreatePromptModal: React.FC<CreatePromptModalProps> = ({
  open,
  onClose,
  onSave,
  editingPrompt,
}) => {
  const [title, setTitle] = useState(editingPrompt?.title || '');
  const [description, setDescription] = useState(editingPrompt?.description || '');
  const [template, setTemplate] = useState(editingPrompt?.template || '');
  const [variables, setVariables] = useState<PromptVariable[]>(
    editingPrompt?.variables || []
  );

  // Reset form when modal opens/closes or editingPrompt changes
  React.useEffect(() => {
    if (open) {
      setTitle(editingPrompt?.title || '');
      setDescription(editingPrompt?.description || '');
      setTemplate(editingPrompt?.template || '');
      setVariables(editingPrompt?.variables || []);
    }
  }, [open, editingPrompt]);

  // Extract variables from template
  const extractVariablesFromTemplate = useCallback((text: string) => {
    const regex = /\{\{(\w+)\}\}/g;
    const matches: string[] = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (!matches.includes(match[1])) {
        matches.push(match[1]);
      }
    }
    return matches;
  }, []);

  // Update variables when template changes
  const handleTemplateChange = (newTemplate: string) => {
    setTemplate(newTemplate);

    const extractedKeys = extractVariablesFromTemplate(newTemplate);

    // Keep existing variables that are still in template, add new ones
    setVariables((prev) => {
      const updated: PromptVariable[] = [];

      extractedKeys.forEach((key) => {
        const existing = prev.find((v) => v.key === key);
        if (existing) {
          updated.push(existing);
        } else {
          updated.push({
            key,
            label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
            placeholder: '',
          });
        }
      });

      return updated;
    });
  };

  const handleVariableChange = (
    index: number,
    field: 'label' | 'placeholder',
    value: string
  ) => {
    setVariables((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSave = () => {
    if (!title.trim() || !template.trim()) return;

    const prompt: Prompt = {
      id: editingPrompt?.id || `custom-${Date.now()}`,
      title: title.trim(),
      description: description.trim(),
      template: template.trim(),
      variables,
      icon: 'Create',
    };

    onSave(prompt);
    handleClose();
  };

  const handleClose = () => {
    onClose();
  };

  const isValid = title.trim() && template.trim();

  if (!open) return null;

  return createPortal(
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {editingPrompt ? 'Editar Prompt' : 'Criar Novo Prompt'}
          </ModalTitle>
          <CloseButton onClick={handleClose} aria-label="Fechar">
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FormSection>
            <FormGroup>
              <FormLabel htmlFor="prompt-title">Título *</FormLabel>
              <FormInput
                id="prompt-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Análise de Documento"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="prompt-description">Descrição</FormLabel>
              <FormInput
                id="prompt-description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Uma breve descrição do que este prompt faz"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="prompt-template">
                Template *
                <FormHint>
                  Use {"{{nome_variavel}}"} para criar campos preenchíveis
                </FormHint>
              </FormLabel>
              <FormTextArea
                id="prompt-template"
                value={template}
                onChange={(e) => handleTemplateChange(e.target.value)}
                placeholder={`Ex: Analise o documento sobre {{tema}} e extraia os seguintes pontos:\n\n1. {{ponto_1}}\n2. {{ponto_2}}\n\nFormate a resposta como {{formato}}.`}
                rows={8}
              />
            </FormGroup>
          </FormSection>

          {variables.length > 0 && (
            <VariablesSection>
              <SectionTitle>
                Variáveis Detectadas ({variables.length})
              </SectionTitle>
              <SectionHint>
                Configure o label e placeholder para cada variável
              </SectionHint>
              <VariablesList>
                {variables.map((variable, index) => (
                  <VariableItem key={variable.key}>
                    <VariableKey>{`{{${variable.key}}}`}</VariableKey>
                    <VariableFields>
                      <VariableFieldGroup>
                        <VariableFieldLabel>Label</VariableFieldLabel>
                        <VariableInput
                          type="text"
                          value={variable.label}
                          onChange={(e) =>
                            handleVariableChange(index, 'label', e.target.value)
                          }
                          placeholder="Nome do campo"
                        />
                      </VariableFieldGroup>
                      <VariableFieldGroup>
                        <VariableFieldLabel>Placeholder</VariableFieldLabel>
                        <VariableInput
                          type="text"
                          value={variable.placeholder}
                          onChange={(e) =>
                            handleVariableChange(index, 'placeholder', e.target.value)
                          }
                          placeholder="Ex: Digite o tema aqui..."
                        />
                      </VariableFieldGroup>
                    </VariableFields>
                  </VariableItem>
                ))}
              </VariablesList>
            </VariablesSection>
          )}
        </ModalBody>

        <ModalFooter>
          <CancelButton onClick={handleClose}>Cancelar</CancelButton>
          <SaveButton onClick={handleSave} disabled={!isValid}>
            {editingPrompt ? 'Salvar Alterações' : 'Criar Prompt'}
          </SaveButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default CreatePromptModal;

// ============ Styled Components ============

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: ${({ theme }) => theme.modal_overlay};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10001;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.modal_background};
  color: ${({ theme }) => theme.font_color};
  border: 1px solid ${({ theme }) => theme.modal_border};
  border-radius: 16px;
  max-width: 700px;
  width: min(94vw, 700px);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.modal_border};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.sidebar_scrollbar};
    border-radius: 3px;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.font_color};
`;

const FormHint = styled.span`
  font-size: 0.8rem;
  font-weight: 400;
  color: ${({ theme }) => theme.text_secondary};
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.prompt_variable_border};
  border-radius: 8px;
  font-size: 0.95rem;
  background: ${({ theme }) => theme.prompt_variable_bg};
  color: ${({ theme }) => theme.font_color};
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.blue_color};
  }

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary};
    opacity: 0.7;
  }
`;

const FormTextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.prompt_variable_border};
  border-radius: 8px;
  font-size: 0.95rem;
  background: ${({ theme }) => theme.prompt_variable_bg};
  color: ${({ theme }) => theme.font_color};
  resize: vertical;
  min-height: 150px;
  font-family: inherit;
  line-height: 1.5;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.blue_color};
  }

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary};
    opacity: 0.7;
  }
`;

const VariablesSection = styled.div`
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.modal_border};
`;

const SectionTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
`;

const SectionHint = styled.p`
  margin: 0 0 16px 0;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.text_secondary};
`;

const VariablesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const VariableItem = styled.div`
  padding: 14px 16px;
  background: ${({ theme }) => theme.prompt_variable_bg};
  border: 1px solid ${({ theme }) => theme.prompt_variable_border};
  border-radius: 10px;
`;

const VariableKey = styled.code`
  display: inline-block;
  padding: 4px 10px;
  background: ${({ theme }) => theme.prompt_category_icon_bg};
  color: ${({ theme }) => theme.blue_color};
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 12px;
`;

const VariableFields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const VariableFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const VariableFieldLabel = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.text_secondary};
`;

const VariableInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.prompt_variable_border};
  border-radius: 6px;
  font-size: 0.9rem;
  background: ${({ theme }) => theme.modal_background};
  color: ${({ theme }) => theme.font_color};
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.blue_color};
  }

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary};
    opacity: 0.7;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.modal_border};
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border: 1px solid ${({ theme }) => theme.modal_border};
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const SaveButton = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.send_button_bg};
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.send_button_hover};
  }

  &:disabled {
    background: ${({ theme }) => theme.send_button_disabled};
    cursor: not-allowed;
  }
`;
